<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { CookieRestClient } from '@idem/common/rest/cookie-rest-client';
import NombreDeSeccion from '@frontend/secciones/components/nombre-de-seccion.vue';
import OpcionActividad from '@frontend/secciones/components/opcion-actividad.vue';
import TarjetaActividad from '@frontend/secciones/components/tarjeta-actividad.vue';
import { enlaceDeSeccion, textosSecciones as textos } from '@frontend/secciones/textos';

interface Actividad {
  id: string;
  nombre: string;
  descripcion: string;
  foto: string | null;
}

interface Seccion {
  id: string;
  nombre: string;
  slug: string;
  actividades: Actividad[];
}

const rest = new CookieRestClient();
const secciones = ref<Seccion[]>([]);
const publicadas = ref<Actividad[]>([]);
const nombre = ref('');
const elegidas = ref<string[]>([]);

async function cargar() {
  secciones.value = await rest.get('secciones');
}

async function cargarPublicadas() {
  publicadas.value = await rest.get('secciones/actividades/publicadas');
}

const aPoner = ref<Record<string, string>>({});

function paraPoner(seccion: Seccion): Actividad[] {
  return publicadas.value.filter(actividad => !seccion.actividades.some(puesta => puesta.id === actividad.id));
}

async function poner(seccion: Seccion) {
  await rest.put(`secciones/${seccion.id}/actividades/${aPoner.value[seccion.id]}`, {});
  await cargar();
}

async function sacar(seccion: Seccion, actividad: Actividad) {
  await rest.delete(`secciones/${seccion.id}/actividades/${actividad.id}`);
  await cargar();
}

async function terminar(seccion: Seccion) {
  await rest.delete(`secciones/${seccion.id}`);
  await cargar();
}

function copiarEnlace(seccion: Seccion) {
  return navigator.clipboard.writeText(enlaceDeSeccion(seccion.slug));
}

const aviso = ref('');

async function crear() {
  aviso.value = '';
  try {
    await rest.post('secciones', { nombre: nombre.value, actividades: elegidas.value });
  } catch (error: any) {
    if (error?.response?.status !== 409) throw error;
    aviso.value = textos.nombreEnUso;
    return;
  }
  nombre.value = '';
  elegidas.value = [];
  await cargar();
}

onMounted(() => Promise.all([cargar(), cargarPublicadas()]));
</script>

<template>
  <section class="mx-auto max-w-5xl px-4 py-8">
    <h1 class="mb-6 text-3xl font-bold">{{ textos.titulo }}</h1>
    <ul class="mb-10 flex flex-col gap-6">
      <li
        v-for="seccion in secciones"
        :key="seccion.id"
        :data-testid="`seccion-${seccion.slug}`"
        class="rounded-2xl border border-gray-200 bg-gray-50 p-5 shadow-sm"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <nombre-de-seccion :seccion="seccion" @renombrada="cargar" />
            <div class="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <span data-testid="enlace" class="truncate text-gray-600">{{ enlaceDeSeccion(seccion.slug) }}</span>
              <button data-testid="copiar-enlace" class="text-blue-600 hover:underline" @click="copiarEnlace(seccion)">
                {{ textos.copiarEnlace }}
              </button>
            </div>
          </div>
          <button
            data-testid="terminar-seccion"
            class="shrink-0 rounded-md border border-red-600 px-3 py-1 text-sm text-red-600 hover:bg-red-50"
            @click="terminar(seccion)"
          >
            {{ textos.terminar }}
          </button>
        </div>
        <ul class="mt-4 grid grid-cols-1 gap-[10px] sm:grid-cols-2 lg:grid-cols-3">
          <tarjeta-actividad
            v-for="actividad in seccion.actividades"
            :key="actividad.id"
            :actividad="actividad"
            @sacar="sacar(seccion, actividad)"
          />
        </ul>
        <div class="mt-4 flex gap-2">
          <select v-model="aPoner[seccion.id]" data-testid="actividad-para-poner" class="rounded border bg-white px-2 py-1 text-sm">
            <option v-for="actividad in paraPoner(seccion)" :key="actividad.id" :value="actividad.id">
              {{ actividad.nombre }}
            </option>
          </select>
          <button
            data-testid="poner"
            :disabled="!aPoner[seccion.id]"
            class="rounded-md border bg-white px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            @click="poner(seccion)"
          >
            {{ textos.poner }}
          </button>
        </div>
      </li>
    </ul>
    <div class="rounded-2xl border border-gray-200 p-5">
      <h2 class="mb-2 text-xl font-semibold">{{ textos.nueva }}</h2>
      <label class="block text-sm font-medium text-gray-700">
        {{ textos.etiquetaNombre }}
        <input v-model="nombre" data-testid="nombre-seccion" class="mt-1 w-full rounded border px-3 py-2" />
      </label>
      <fieldset class="mt-4">
        <legend class="text-sm font-medium text-gray-700">{{ textos.etiquetaActividades }}</legend>
        <div class="mt-2 grid grid-cols-2 gap-[10px] sm:grid-cols-3 lg:grid-cols-4">
          <opcion-actividad v-for="actividad in publicadas" :key="actividad.id" v-model="elegidas" :actividad="actividad" />
        </div>
      </fieldset>
      <button
        data-testid="crear-seccion"
        :disabled="!nombre"
        class="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        @click="crear"
      >
        {{ textos.crear }}
      </button>
      <p v-if="aviso" data-testid="aviso-crear-seccion" class="mt-2 text-sm text-red-600">{{ aviso }}</p>
    </div>
  </section>
</template>
